// fotobook_image_processor.js
// Utilidades para procesar y optimizar imágenes en el flujo de Fotobooks
// - NO usa Pintura; trabaja directamente con File, Image y canvas
// - Pensado para integrarse con FotobookEditor / FotobookPhotoSelector
//
// Convenciones:
// - Todas las dimensiones que recibe este módulo (slotBox, pageWidth, pageHeight)
//   se asumen en píxeles a 300 DPI, coherentes con FOTOBOOK_CONFIG.

/**
 * Carga un File de imagen en un objeto HTMLImageElement.
 * @param {File|Blob} file
 * @returns {Promise<HTMLImageElement>}
 */
export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Archivo de imagen no proporcionado'));
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err || new Error('No se pudo cargar la imagen'));
    };

    img.src = url;
  });
}

/**
 * Calcula el tamaño objetivo para una imagen dentro de un slot de página.
 * Asume que slotBox está en píxeles a 300 DPI (mismas unidades que el SVG).
 *
 * @param {{ w: number, h: number }} slotBox - Dimensiones del slot (px @300DPI)
 * @param {{ dpi?: number, maxDimension?: number, scaleMultiplier?: number }} [options]
 * @returns {{ targetWidth: number, targetHeight: number }}
 */
export function calculateTargetSizeForSlot(slotBox, options = {}) {
  const {
    dpi = 300,
    // límite "duro" de seguridad para evitar bitmaps enormes
    maxDimension = 6000,
    // multiplicador por si se quiere forzar un poco más de resolución (p.ej. 1.0–1.2)
    scaleMultiplier = 1.0
  } = options;

  if (!slotBox || !Number.isFinite(slotBox.w) || !Number.isFinite(slotBox.h)) {
    return { targetWidth: 0, targetHeight: 0 };
  }

  // slotBox.w / h ya están en px a 300DPI (por viewBox = pageWidth/pageHeight).
  let targetWidth = Math.max(1, Math.round(slotBox.w * scaleMultiplier));
  let targetHeight = Math.max(1, Math.round(slotBox.h * scaleMultiplier));

  // Normalizar si el usuario cambia el DPI en el futuro
  if (dpi !== 300 && dpi > 0) {
    const factor = dpi / 300;
    targetWidth = Math.round(targetWidth * factor);
    targetHeight = Math.round(targetHeight * factor);
  }

  // Clamp de seguridad para evitar imágenes gigantes
  const clamp = (value) => Math.min(maxDimension, Math.max(1, value));
  targetWidth = clamp(targetWidth);
  targetHeight = clamp(targetHeight);

  return { targetWidth, targetHeight };
}

/**
 * Dibuja una imagen en un canvas aplicando modo "cover" (object-fit: cover):
 * - Se mantiene el aspect ratio de la imagen.
 * - Se rellena completamente el canvas, recortando el sobrante si es necesario.
 *
 * @param {HTMLImageElement} image
 * @param {number} targetWidth
 * @param {number} targetHeight
 * @returns {HTMLCanvasElement}
 */
export function drawImageToCanvasCover(image, targetWidth, targetHeight) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(targetWidth));
  canvas.height = Math.max(1, Math.round(targetHeight));

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const imgWidth = image.naturalWidth || image.width;
  const imgHeight = image.naturalHeight || image.height;

  if (!imgWidth || !imgHeight) {
    // En caso extremo, dejar canvas vacío
    return canvas;
  }

  const scale = Math.max(targetWidth / imgWidth, targetHeight / imgHeight);
  const drawWidth = imgWidth * scale;
  const drawHeight = imgHeight * scale;

  const offsetX = (targetWidth - drawWidth) / 2;
  const offsetY = (targetHeight - drawHeight) / 2;

  ctx.clearRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

  return canvas;
}

/**
 * Convierte un canvas a Blob JPEG con la calidad deseada.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {number} quality - 0.0 a 1.0
 * @returns {Promise<Blob>}
 */
export function canvasToJpegBlob(canvas, quality = 0.9) {
  return new Promise((resolve, reject) => {
    if (!canvas || typeof canvas.toBlob !== 'function') {
      reject(new Error('Canvas inválido o método toBlob no disponible'));
      return;
    }

    const q = Number.isFinite(quality) ? Math.min(1, Math.max(0.1, quality)) : 0.9;

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('No se pudo generar el Blob JPEG'));
      } else {
        resolve(blob);
      }
    }, 'image/jpeg', q);
  });
}

/**
 * Redimensiona un File de imagen a un JPEG optimizado usando modo "cover",
 * pensado para ajustarse exactamente a un slot de fotobook.
 *
 * @param {File|Blob} file
 * @param {number} targetWidth
 * @param {number} targetHeight
 * @param {{ quality?: number }} [options]
 * @returns {Promise<{ blob: Blob, width: number, height: number }>}
 */
export async function resizeImageFileToJpeg(file, targetWidth, targetHeight, options = {}) {
  const { quality = 0.9 } = options;

  const image = await loadImageFromFile(file);
  const canvas = drawImageToCanvasCover(image, targetWidth, targetHeight);
  const blob = await canvasToJpegBlob(canvas, quality);

  return {
    blob,
    width: canvas.width,
    height: canvas.height
  };
}

/**
 * Flujo completo para procesar una imagen de fotobook en función de un slot.
 * - Calcula tamaño objetivo desde slotBox (px @300DPI).
 * - Redimensiona la imagen a JPEG optimizado.
 *
 * @param {File|Blob} file
 * @param {{ w: number, h: number }} slotBox
 * @param {{ dpi?: number, maxDimension?: number, scaleMultiplier?: number, quality?: number }} [options]
 * @returns {Promise<{ blob: Blob, width: number, height: number }>}
 */
export async function processSlotImage(file, slotBox, options = {}) {
  const { quality, ...sizeOptions } = options;
  const { targetWidth, targetHeight } = calculateTargetSizeForSlot(slotBox, sizeOptions);

  if (!targetWidth || !targetHeight) {
    throw new Error('Dimensiones de slot inválidas para procesar la imagen');
  }

  return resizeImageFileToJpeg(file, targetWidth, targetHeight, { quality });
}

/**
 * Optimiza una imagen genérica (redimensiona manteniendo el aspect ratio y convierte a JPEG).
 *
 * @param {File|Blob} file
 * @param {{ maxDimension?: number, quality?: number }} [options]
 * @returns {Promise<{ blob: Blob, width: number, height: number }>}
 */
export async function optimizeImage(file, options = {}) {
  const { maxDimension = 2500, quality = 0.8 } = options;

  const image = await loadImageFromFile(file);
  let { width, height } = image;

  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, width, height);

  const blob = await canvasToJpegBlob(canvas, quality);

  return {
    blob,
    width,
    height
  };
}


